import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input, Textarea, Select } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { projectsApi } from '../../api/projects.api'
import { ApiError } from '../../api/client'
import { toast } from '../../store/toastStore'
import type { Project, ProjectStatus } from '../../api/types'

const STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: 'PLANNING', label: 'Planung' },
  { value: 'ACTIVE', label: 'Aktiv' },
  { value: 'PAUSED', label: 'Pausiert' },
  { value: 'COMPLETED', label: 'Abgeschlossen' },
  { value: 'ARCHIVED', label: 'Archiviert' },
]

export function ProjectFormModal({ project, onClose }: { project?: Project | null; onClose: () => void }) {
  const isEdit = Boolean(project)
  const queryClient = useQueryClient()

  const [name, setName] = useState(project?.name ?? '')
  const [description, setDescription] = useState(project?.description ?? '')
  const [status, setStatus] = useState<ProjectStatus>(project?.status ?? 'PLANNING')
  const [color, setColor] = useState(project?.color ?? '#6366f1')
  const [targetDate, setTargetDate] = useState(project?.targetDate?.slice(0, 10) ?? '')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () =>
      isEdit && project
        ? projectsApi.update(project.id, { name, description, status, color, targetDate: targetDate || null })
        : projectsApi.create({ name, description, status, color, targetDate: targetDate || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success(isEdit ? 'Projekt aktualisiert.' : 'Projekt angelegt.')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen.'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate()
  }

  return (
    <Modal
      title={isEdit ? 'Projekt bearbeiten' : 'Neues Projekt'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={mutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}

        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <Field label="Name" htmlFor="proj-name">
              <Input id="proj-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            </Field>
          </div>
          <div style={{ width: 100 }}>
            <Field label="Farbe" htmlFor="proj-color">
              <Input id="proj-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ padding: 4 }} />
            </Field>
          </div>
        </div>

        <Field label="Beschreibung (optional)" htmlFor="proj-description">
          <Textarea id="proj-description" value={description ?? ''} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <Field label="Status" htmlFor="proj-status">
              <Select id="proj-status" value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)}>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Zieldatum (optional)" htmlFor="proj-target">
              <Input id="proj-target" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
            </Field>
          </div>
        </div>
      </form>
    </Modal>
  )
}
